import { FormEvent, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData } from 'react-router-dom'

import { useCurrentHealthCenter } from '../../components/AppShell'
import { ProfessionalAttributes, createProfessional, fetchNewProfessional } from '../../api/professionals'
import { ErrorList, Panel, PanelBody, RutField, SelectField, SubmitButton, TextField } from '../../components/common'
import { ApiError } from '../../lib/api'
import { useNavigateTo } from '../../lib/spaPath'
import styles from './NewProfessionalPage.module.scss'

const EMPTY_FORM: Omit<ProfessionalAttributes, 'profession_id'> = {
  rut: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  password_confirmation: '',
}

const iconLabel = (icon: string, label: string) => (
  <>
    <i className={`fa ${icon}`} /> {label}
  </>
)

export function loader({ params }: LoaderFunctionArgs) {
  return fetchNewProfessional(Number(params.healthCenterId))
}

export default function NewProfessionalPage() {
  const healthCenter = useCurrentHealthCenter()
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const [form, setForm] = useState(EMPTY_FORM)
  const [professionId, setProfessionId] = useState(data.default_profession_id)
  const [errors, setErrors] = useState<readonly string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const navigateTo = useNavigateTo()

  if (!healthCenter) {
    return null
  }

  const set = (field: keyof typeof EMPTY_FORM) => (value: string) => setForm((current) => ({ ...current, [field]: value }))

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    setErrors([])
    try {
      const result = await createProfessional(healthCenter.id, { ...form, profession_id: professionId })
      navigateTo(result.redirect, { notice: result.notice })
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as { errors?: string[] } | null) : null
      setErrors(body?.errors ?? ['Hubo un error al crear la cuenta. Contactate a los administradores para solucionar el problema'])
      setSubmitting(false)
    }
  }

  return (
    <div className={`col-lg-8 col-lg-offset-2 ${styles.page}`}>
      <Panel>
        <form className="professional" onSubmit={(event) => void handleSubmit(event)}>
          <PanelBody>
            <div className="col">
              <ErrorList messages={errors} />

              <div className="module-heading">
                <h3 className={`text-center ${styles.sectionHeading}`}>Información General</h3>
                <div className="col-xs-12">
                  <RutField
                    className="col-sm-6 col-xs-12"
                    label={iconLabel('fa-id-card-o', 'Rut')}
                    value={form.rut}
                    onChange={set('rut')}
                  />
                  <TextField className="col-sm-6 col-xs-12" label={iconLabel('fa-user', 'Nombre')} value={form.name} onChange={set('name')} />
                  <TextField
                    className="col-sm-6 col-xs-12"
                    label={iconLabel('fa-envelope', 'Email')}
                    value={form.email}
                    onChange={set('email')}
                  />
                  <TextField
                    className="col-sm-6 col-xs-12"
                    label={iconLabel('fa-phone', 'Teléfono')}
                    value={form.phone}
                    onChange={set('phone')}
                  />
                  <TextField
                    className="col-sm-6 col-xs-12"
                    label={iconLabel('fa-key', 'Contraseña')}
                    type="password"
                    value={form.password}
                    onChange={set('password')}
                  />
                  <TextField
                    className="col-sm-6 col-xs-12"
                    label={iconLabel('fa-key', 'Confirmar contraseña')}
                    type="password"
                    value={form.password_confirmation}
                    onChange={set('password_confirmation')}
                  />
                  <SelectField
                    className="col-sm-6 col-xs-12"
                    label="Profesión"
                    controlClassName="profession-selector"
                    controlStyle={{ width: '100%' }}
                    value={professionId}
                    onChange={(value) => setProfessionId(Number(value))}
                    options={data.professions.map((profession) => ({ value: profession.id, label: profession.title }))}
                  />
                </div>
                <div className="text-right">
                  <SubmitButton label="Crear Profesional" raised className={styles.submit} disabled={submitting} />
                </div>
              </div>
            </div>
          </PanelBody>
        </form>
      </Panel>
    </div>
  )
}
